import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  Bell, Target, ArrowRight, Sparkles, Crown, Trophy
} from 'lucide-react';

// Grabbit Logo
const GRABBIT_LOGO = "https://customer-assets.emergentagent.com/job_8dd61eda-1b76-4ded-9514-b53b1f23bf59/artifacts/uppz6d01_Grabbit.png";

const CATEGORIES = [
  { id: 'beauty', name: 'Beauty', icon: '💄', color: '#F9A8D4' },
  { id: 'clothing', name: 'Clothing', icon: '👕', color: '#7DD3FC' },
  { id: 'shoes', name: 'Shoes', icon: '👟', color: '#4ADE80' },
  { id: 'home', name: 'Home', icon: '🪑', color: '#FCD34D' },
  { id: 'tech', name: 'Tech', icon: '📱', color: '#A78BFA' },
  { id: 'travel', name: 'Travel', icon: '✈️', color: '#2DD4BF' },
  { id: 'gifts', name: 'Gifts', icon: '🎁', color: '#FB923C' },
  { id: 'flash', name: 'Flash Deals', icon: '⚡', color: '#F87171' },
];

const QUOTES = [
  "Tell us what you want. We'll hunt the deals.",
  "Smart bunnies wait for the best deals. 🐰",
  "Grab it when the price drops — not before.",
  "The best deals find those who are prepared to hop on them.",
];

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuote, setCurrentQuote] = useState(0);
  const [discountValue, setDiscountValue] = useState(40);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote((prev) => (prev + 1) % QUOTES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const getSliderBackground = (value) => {
    const greenStart = Math.min(value * 2, 100);
    return `linear-gradient(90deg, #27272A ${100 - value}%, #34D399 ${100 - value}%)`;
  };

  const getDiscountLabel = () => {
    if (discountValue >= 50) return { text: '🔥 Steal Mode!', color: '#F87171' };
    if (discountValue >= 40) return { text: '🎯 Great Target', color: '#FB923C' };
    if (discountValue >= 30) return { text: '✓ Good Deal', color: '#4ADE80' };
    return { text: 'Moderate', color: '#60A5FA' };
  };

  const discountLabel = getDiscountLabel();

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Floating Navbar */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-6 left-1/2 -translate-x-1/2 w-[90%] max-w-2xl glass rounded-full px-6 py-3 z-50 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <img src={GRABBIT_LOGO} alt="Grabbit" className="h-10" />
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/login')}
            className="text-muted-foreground hover:text-foreground"
            data-testid="nav-login-btn"
          >
            Log in
          </Button>
          <Button 
            onClick={() => navigate('/signup')}
            className="bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full px-6 shadow-lg hover:shadow-[0_0_30px_rgba(244,114,182,0.4)] transition-all"
            data-testid="nav-signup-btn"
          >
            Get Started
          </Button>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/10 to-purple-600/10 border border-pink-500/20">
                <span className="text-lg">🐰</span>
                <span className="text-sm font-medium bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">Smart Deals. Powered by Community.</span>
              </div>
              
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1]">
                Grab it
                <br />
                <span className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">before it's gone.</span>
              </h1>
              
              <motion.p 
                key={currentQuote}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-xl text-muted-foreground max-w-lg"
              >
                "{QUOTES[currentQuote]}"
              </motion.p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/signup')}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-[0_0_40px_rgba(244,114,182,0.5)] transition-all group"
                  data-testid="hero-get-started-btn"
                >
                  Start Hunting Deals
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="rounded-full px-8 py-6 text-lg border-border/50 hover:border-pink-500/50 hover:bg-pink-500/5"
                  data-testid="hero-login-btn"
                >
                  I have an account
                </Button>
              </div>
            </motion.div>

            {/* Right Content - Interactive Demo */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative"
            >
              <div className="glass rounded-3xl p-8 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-xl font-semibold">Set Your Deal Alert</h3>
                  <Bell className="w-5 h-5 text-muted-foreground" />
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Alert me at</span>
                    <div className="text-right">
                      <span className="text-3xl font-heading font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">{discountValue}% off</span>
                      <p className="text-xs" style={{ color: discountLabel.color }}>{discountLabel.text}</p>
                    </div>
                  </div>
                  
                  <input 
                    type="range"
                    min="10"
                    max="80"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(parseInt(e.target.value))}
                    className="discount-slider w-full h-3 rounded-full cursor-pointer"
                    style={{ background: getSliderBackground(discountValue) }}
                    data-testid="demo-discount-slider"
                  />
                  
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>10%</span>
                    <span>30%</span>
                    <span>50% 🔥</span>
                    <span>80%</span>
                  </div>
                </div>

                <motion.div 
                  animate={{ 
                    scale: discountValue >= 50 ? [1, 1.02, 1] : 1,
                    boxShadow: discountValue >= 50 
                      ? ['0 0 0 rgba(244, 114, 182, 0)', '0 0 30px rgba(244, 114, 182, 0.3)', '0 0 0 rgba(244, 114, 182, 0)']
                      : '0 0 0 rgba(244, 114, 182, 0)'
                  }}
                  transition={{ duration: 0.5, repeat: discountValue >= 50 ? Infinity : 0, repeatDelay: 1 }}
                  className="p-4 rounded-xl bg-card border border-border/50"
                >
                  <p className="text-sm text-muted-foreground">
                    {discountValue < 30 && "🐰 Set a higher threshold for better deals!"}
                    {discountValue >= 30 && discountValue < 50 && "🐰 Good target! You'll catch most sales."}
                    {discountValue >= 50 && discountValue < 70 && "🔥 Steal Mode! Only the best drops for you."}
                    {discountValue >= 70 && "🏆 Patient hunter! You're going for legendary deals."}
                  </p>
                </motion.div>
              </div>

              {/* Floating notification preview */}
              <motion.div 
                initial={{ opacity: 0, y: 20, x: 20 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute -bottom-4 -right-4 glass rounded-2xl p-4 max-w-xs shadow-2xl"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                    <Bell className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">🔥 45% off Nike Air Max!</p>
                    <p className="text-xs text-muted-foreground mt-1">Just dropped from $180 to $99</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Grab deals in any category
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From beauty to tech, shoes to home - tell us what you want and we'll hunt it for you.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {CATEGORIES.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="group cursor-pointer"
                data-testid={`category-${category.id}`}
              >
                <div className="glass rounded-2xl p-4 h-full flex flex-col items-center gap-2 border border-transparent hover:border-pink-500/30 transition-all">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-all group-hover:scale-110 text-2xl"
                    style={{ backgroundColor: `${category.color}20` }}
                  >
                    {category.icon}
                  </div>
                  <span className="font-medium text-sm text-center">{category.name}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              How it works
            </h2>
            <p className="text-muted-foreground text-lg">
              Three simple steps to smarter shopping
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: 1, title: 'Tell us what you want', desc: 'Select category, brands, and specific items you\'re hunting.', icon: Target, color: '#F472B6', emoji: '🐰' },
              { step: 2, title: 'Set your alert', desc: 'Use the discount slider to set your target percentage off.', icon: Sparkles, color: '#FACC15', emoji: '🔔' },
              { step: 3, title: 'Grab it instantly', desc: 'We\'ll ping you the moment your deal drops with a direct link.', icon: Trophy, color: '#34D399', emoji: '🎯' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="glass rounded-3xl p-8 relative overflow-hidden group"
              >
                <div className="absolute top-4 right-4 text-6xl font-heading font-bold opacity-10">
                  {item.step}
                </div>
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform text-2xl"
                  style={{ backgroundColor: `${item.color}20` }}
                >
                  {item.emoji}
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-12 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 via-transparent to-purple-600/10" />
            <div className="relative z-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Ready to grab smarter deals? 🐰
              </h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
                Join thousands of smart shoppers who let deals come to them.
              </p>
              <Button 
                size="lg"
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-[0_0_40px_rgba(244,114,182,0.5)] transition-all"
                data-testid="cta-get-started-btn"
              >
                Start Hunting Free
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={GRABBIT_LOGO} alt="Grabbit" className="h-8" />
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 Grabbit. Smart Deals. Powered by Community.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
